import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TruckIcon, 
  GlobeAltIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  CheckCircleIcon,
  StarIcon,
  ArrowRightIcon,
  CubeIcon,
  BoltIcon,
  MapPinIcon,
  ChartBarIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  RocketLaunchIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import Navbar from '../components/Navbar';

function Home() {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleTrack = () => {
    if (trackingNumber.trim()) {
      navigate(`/track?number=${trackingNumber}`);
    }
  };

  const handleNewShipment = () => {
    navigate('/new-shipment');
  };

  const solutions = [
    {
      icon: GlobeAltIcon,
      title: "International Trade",
      description: "Seamless cross-border logistics worldwide",
      color: "bg-orange-500"
    },
    {
      icon: BuildingOfficeIcon,
      title: "Warehouse Solutions",
      description: "State-of-the-art facilities in major cities across the globe",
      color: "bg-purple-600"
    },
    {
      icon: ChartBarIcon,
      title: "Supply Chain",
      description: "End-to-end logistics management for businesses of all sizes",
      color: "bg-green-600"
    },
    {
      icon: TruckIcon,
      title: "Express Delivery",
      description: "Fast, reliable delivery across countries and continents worldwide",
      color: "bg-blue-600"
    }
  ];

  const benefits = [
    {
      number: "01",
      title: "Global Coverage",
      description: "Serving customers worldwide with reliable delivery networks spanning across continents and time zones."
    },
    {
      number: "02",
      title: "Real-Time Visibility",
      description: "Track your shipments live with our advanced GPS-enabled tracking system. Know exactly where your package is at any moment."
    },
    {
      number: "03",
      title: "Competitive Pricing",
      description: "Transparent, affordable rates with no hidden fees. Get the best value for your logistics needs worldwide."
    },
    {
      number: "04",
      title: "Local Expertise",
      description: "Our team understands international logistics challenges and customs regulations. We make shipping simple."
    }
  ];

  const stats = [
    { number: "150K+", label: "Packages Delivered", icon: CubeIcon },
    { number: "50+", label: "Countries Served", icon: MapPinIcon },
    { number: "98.2%", label: "On-Time Delivery", icon: ClockIcon },
    { number: "4.8/5", label: "Customer Rating", icon: StarIcon },
    { number: "24/7", label: "Support Available", icon: PhoneIcon },
    { number: "100+", label: "Cities Worldwide", icon: GlobeAltIcon }
  ];

  const processes = [
    {
      step: "1",
      title: "Book Your Shipment",
      description: "Create a shipment online or via our mobile app. Choose your service level and get instant pricing.",
      icon: CubeIcon
    },
    {
      step: "2",
      title: "We Collect",
      description: "Schedule a pickup or drop off at one of our convenient locations worldwide.",
      icon: TruckIcon
    },
    {
      step: "3",
      title: "Track in Real-Time",
      description: "Monitor your shipment's journey with live updates and notifications sent directly to your phone.",
      icon: ChartBarIcon
    },
    {
      step: "4",
      title: "Fast Delivery",
      description: "Your package arrives safely and on time, whether it's across town or across the continent.",
      icon: CheckCircleIcon
    }
  ];

  const testimonials = [
    {
      name: "Michael Chen",
      role: "E-commerce Business Owner",
      company: "Global Retail Co.",
      location: "New York, USA",
      quote: "CargoHaul transformed our delivery operations. Their international shipping has expanded our reach globally and boosted customer satisfaction by 40%.",
      rating: 5
    },
    {
      name: "Emma Thompson",
      role: "Operations Manager",
      company: "London Imports Ltd",
      location: "London, UK",
      quote: "Their international shipping service is exceptional. We've expanded our reach across continents with their reliable network.",
      rating: 5
    },
    {
      name: "James Wilson",
      role: "CEO",
      company: "Sydney Distribution",
      location: "Sydney, Australia",
      quote: "Best logistics partner we've worked with. Fast, professional, and always reliable. Highly recommend!",
      rating: 5
    }
  ];

  const cities = [
    { name: "New York", province: "New York, USA", image: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&h=300&fit=crop" },
    { name: "London", province: "England, UK", image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=300&fit=crop" },
    { name: "Tokyo", province: "Japan", image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop" },
    { name: "Sydney", province: "New South Wales, Australia", image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop" },
    { name: "Dubai", province: "United Arab Emirates", image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=300&fit=crop" },
    { name: "Singapore", province: "Singapore", image: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=400&h=300&fit=crop" }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section - Completely New Design */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-0">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-blue-700"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center mix-blend-overlay opacity-20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Content */}
            <div className="text-white text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-md rounded-full mb-4 sm:mb-6 border border-white/20">
                <BoltIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                <span className="text-xs sm:text-sm font-semibold">Fast. Reliable. Global.</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 leading-tight">
                Logistics Made
                <br />
                <span className="text-orange-400">
                  Simple
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
                Your trusted partner for shipping worldwide. From local deliveries to international trade, 
                we deliver excellence with speed, reliability, and global expertise.
              </p>

              {/* Quick Actions */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <button
                  onClick={handleNewShipment}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 text-white rounded-xl font-bold text-base sm:text-lg hover:bg-orange-600 hover:shadow-2xl hover:scale-105 transition-all w-full sm:w-auto"
                >
                  Ship Now
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-base sm:text-lg hover:bg-white/20 transition-all w-full sm:w-auto"
                >
                  Get Quote
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-4 sm:gap-6 lg:gap-8 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                  <span>Insured Shipments</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  <span>24/7 Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                  <span>Global Coverage</span>
                </div>
              </div>
            </div>

            {/* Right Content - Tracking Box */}
            <div className="mt-8 lg:mt-0 lg:pl-8">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl border border-white/20">
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-1 sm:mb-2">Track Your Package</h3>
                  <p className="text-sm sm:text-base text-gray-600">Enter your tracking number below</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                    placeholder="Enter tracking number"
                    className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white text-sm sm:text-base lg:text-lg font-medium transition-all"
                  />
                  <button
                    onClick={handleTrack}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white rounded-xl font-bold text-sm sm:text-base hover:bg-blue-700 hover:shadow-xl transition-all transform hover:scale-105 w-full sm:w-auto"
                  >
                    Track
                  </button>
                </div>

                <div className="pt-4 sm:pt-6 border-t border-gray-200">
                  <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">Quick Links</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    <button
                      onClick={handleNewShipment}
                      className="p-3 sm:p-4 bg-gray-50 hover:bg-orange-50 rounded-xl text-left transition-colors group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg group-hover:bg-orange-500 transition-colors">
                          <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-sm sm:text-base text-gray-900">Create Shipment</p>
                          <p className="text-xs text-gray-500">Start shipping now</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate('/services')}
                      className="p-3 sm:p-4 bg-gray-50 hover:bg-blue-50 rounded-xl text-left transition-colors group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg group-hover:bg-blue-500 transition-colors">
                          <GlobeAltIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-sm sm:text-base text-gray-900">Our Services</p>
                          <p className="text-xs text-gray-500">View options</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce hidden sm:block">
          <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white/60 rotate-90" />
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-xl mb-2 sm:mb-3">
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-1">{stat.number}</div>
                <div className="text-gray-400 text-xs sm:text-sm font-medium leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Complete Logistics Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From local deliveries to international trade, we provide end-to-end logistics services 
              tailored for businesses worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-300"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 ${solution.color} rounded-2xl mb-6 group-hover:scale-110 transition-transform`}>
                  <solution.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600 leading-relaxed">{solution.description}</p>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRightIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Partners Ticker */}
      <section className="py-20 bg-white overflow-hidden border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">
              Our Trusted Partners
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Working with industry leaders to provide comprehensive delivery solutions
            </p>
          </div>
          
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex animate-scroll gap-16">
                {/* First set of logos */}
                <div className="flex items-center gap-16 flex-shrink-0">
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/dhl.png" alt="DHL" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/fedex.svg" alt="FedEx" className="h-12 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/ups.jpg" alt="UPS" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/amazon.svg" alt="Amazon" className="h-12 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/usps.svg" alt="USPS" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/ontrac.webp" alt="OnTrac" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/estes.svg" alt="Estes" className="h-12 object-contain" />
                  </div>
                </div>
                {/* Duplicate set for seamless loop */}
                <div className="flex items-center gap-16 flex-shrink-0">
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/dhl.png" alt="DHL" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/fedex.svg" alt="FedEx" className="h-12 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/ups.jpg" alt="UPS" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/amazon.svg" alt="Amazon" className="h-12 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/usps.svg" alt="USPS" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/ontrac.webp" alt="OnTrac" className="h-14 object-contain" />
                  </div>
                  <div className="flex items-center justify-center h-24 w-40 transition-all duration-300 opacity-90 hover:opacity-100 hover:scale-110">
                    <img src="/images/brands/estes.svg" alt="Estes" className="h-12 object-contain" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, straightforward shipping process designed for efficiency
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-black text-xl">{process.step}</span>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <process.icon className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
                {index < processes.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRightIcon className="w-8 h-8 text-blue-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
                Why Choose CargoHaul
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We're not just another logistics company. We're your global partner, 
                understanding the unique challenges of international shipping and delivering solutions that work.
              </p>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-black text-2xl">{benefit.number}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="Logistics Operations"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gray-900/60"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <TruckIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Operational Excellence</p>
                        <p className="text-sm text-gray-600">State-of-the-art facilities</p>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      Our modern warehouses and distribution centers worldwide ensure 
                      your packages are handled with care and delivered efficiently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities We Serve */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
              Major Cities We Serve
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Comprehensive coverage across major metropolitan areas worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map((city, index) => (
              <div
                key={index}
                className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={city.image}
                    alt={city.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gray-900/60"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-1">{city.name}</h3>
                  <p className="text-gray-300">{city.province}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Trusted by Businesses Worldwide
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what our customers have to say about their experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed text-lg">
                  "{testimonial.quote}"
                </blockquote>
                <div className="pt-6 border-t border-gray-200">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                  <p className="text-sm text-gray-500">{testimonial.company}, {testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-700 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Ready to Ship with Confidence?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join thousands of businesses worldwide who trust CargoHaul 
            for their shipping needs. Get started today and experience the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleNewShipment}
              className="px-10 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-xl"
            >
              Create Your First Shipment
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="px-10 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
            >
              Speak to Our Team
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
